import * as React from 'react';
import {
  Zap, Loader2, X, Eye, Heart, MessageSquare, Bookmark, Video,
  ExternalLink, CheckSquare, Square, AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  getKeywordsSummary, viralFetch, viralImport,
  type ViralItem, type AnalysisKeyword,
} from '@/api/client';
import { ViralVoiceInput } from '@/components/viral-voice-input';
import { showToast } from '@/components/ui/toast';

const STOP_REASONS: Record<string, string> = {
  reached_target: '已达到展示数量', budget_exhausted: '已达到本次搜索预算',
  upstream_end: '本轮可取候选已结束', no_new_qualified: '后续候选没有新增匹配',
  page_limit: '已达到分页上限', cache_hit: '复用近期检索结果',
  page_failed_partial: '部分结果页获取失败',
};

/** 降级模式展示文案（组合词 0 结果 → 单标签重试，全部标签跑完再混合） */
function degradedLabel(groups?: string[] | null): string {
  if (!groups || !groups.length) return '组合词无结果，已自动降级为单标签检索';
  return `组合词无结果，已按单标签分别检索（${groups.join(' / ')}）`;
}

const PLATFORM_DOT: Record<string, string> = {
  douyin: 'bg-sky-500',
  xiaohongshu: 'bg-pink-500',
};

function platformLabel(p: string) {
  if (p === 'xiaohongshu') return '小红书';
  return '抖音';
}

function fmtNum(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function fmtDur(s: number | null | undefined): string {
  if (!s || s <= 0) return '';
  let secs = s;
  if (secs > 10000) secs = Math.floor(secs / 1000);
  const m = Math.floor(secs / 60);
  return `${m}:${String(Math.floor(secs % 60)).padStart(2, '0')}`;
}

function itemKey(it: ViralItem): string {
  return it.item_id || it.note_id || '';
}

type Props = {
  open: boolean;
  onClose: () => void;
  onImported: (saved: number) => void;
};

export default function ViralFetchModal({ open, onClose, onImported }: Props) {
  // ─── 赛道关键词建议（实时，来源 keywords-summary）──────────────
  const [suggestKeywords, setSuggestKeywords] = React.useState<AnalysisKeyword[]>([]);
  const [suggestLoading, setSuggestLoading] = React.useState(false);

  // ─── 关键词搜索栏（圆角标签 chips）────────────────────────────
  const [keywords, setKeywords] = React.useState<string[]>([]);
  const [keywordInput, setKeywordInput] = React.useState('');

  const [description, setDescription] = React.useState('');
  const descriptionInput = React.useRef<HTMLTextAreaElement | null>(null);
  React.useLayoutEffect(() => {
    const input = descriptionInput.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(150, input.scrollHeight)}px`;
  }, [description, open]);
  const [voiceBusy, setVoiceBusy] = React.useState(false);
  const [jobId, setJobId] = React.useState('');
  const [intentSummary, setIntentSummary] = React.useState('');
  const [warnings, setWarnings] = React.useState<string[]>([]);
  const [diagnostics, setDiagnostics] = React.useState<import('@/api/client').ViralFetchResult['diagnostics']>();
  const requestKey = React.useRef({fingerprint: '', id: ''});
  const searchAbort = React.useRef<AbortController | null>(null);
  const searchGeneration = React.useRef(0);
  React.useEffect(() => {
    if (!open) { searchGeneration.current++; searchAbort.current?.abort(); setLoading(false); setVoiceBusy(false); }
    return () => { searchGeneration.current++; searchAbort.current?.abort(); };
  }, [open]);
  const splitKeywords = (text: string) => text.split(/[,，、;；\n]+/).map(w => w.trim()).filter(Boolean);
  const appendTranscript = React.useCallback((text: string) => {
    setDescription(prev => `${prev}${prev.trim() ? ' ' : ''}${text}`.slice(0, 1000));
  }, []);

  // ─── 检索维度 ───────────────────────────────────────────────
  const [platform, setPlatform] = React.useState<'douyin' | 'xiaohongshu'>('douyin');
  const [count, setCount] = React.useState(10);
  const [dateType, setDateType] = React.useState(7);
  const [labelType, setLabelType] = React.useState(0);
  const [durationType, setDurationType] = React.useState(0);
  const [sortType, setSortType] = React.useState('popularity_descending');
  const [noteType, setNoteType] = React.useState('不限');
  const [timeFilter, setTimeFilter] = React.useState('一周内');

  // ─── 抓取结果 ───────────────────────────────────────────────
  const [items, setItems] = React.useState<ViralItem[]>([]);
  const [reviewItems, setReviewItems] = React.useState<ViralItem[]>([]);
  const [rejectedCount, setRejectedCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [importedIds, setImportedIds] = React.useState<Set<string>>(new Set());
  const [importing, setImporting] = React.useState(false);
  const [preview, setPreview] = React.useState<ViralItem | null>(null);

  // 打开弹窗时加载赛道关键词建议
  React.useEffect(() => {
    if (!open) return;
    setSuggestLoading(true);
    getKeywordsSummary()
      .then((d) => setSuggestKeywords(d.keywords || []))
      .catch(() => setSuggestKeywords([]))
      .finally(() => setSuggestLoading(false));
  }, [open]);

  // 关闭时重置结果与勾选
  const handleClose = React.useCallback(() => {
    searchGeneration.current++;
    searchAbort.current?.abort();
    setLoading(false);
    setVoiceBusy(false);
    setDescription('');
    setJobId('');
    setIntentSummary('');
    setWarnings([]);
    setDiagnostics(undefined);
    setPreview(null);
    setItems([]);
    setReviewItems([]);
    setRejectedCount(0);
    setSelected(new Set());
    setImportedIds(new Set());
    setMessage('');
    setKeywords([]);
    setKeywordInput('');
    onClose();
  }, [onClose]);

  const addKeyword = React.useCallback((raw: string) => {
    const words = raw.split(/[,，、;；\n]+/).map(w => w.trim()).filter(Boolean);
    if (!words.length) return;
    setKeywords(prev => [...new Set([...prev, ...words])]);
    setKeywordInput('');
  }, []);

  const removeKeyword = React.useCallback((w: string) => {
    setKeywords((prev) => prev.filter((k) => k !== w));
  }, []);

  const toggleSuggest = React.useCallback((w: string) => {
    setKeywords((prev) => (prev.includes(w) ? prev.filter((k) => k !== w) : [...prev, w]));
  }, []);

  // ─── 立即爬取 ───────────────────────────────────────────────
  const handleFetch = React.useCallback(async () => {
    if (loading || voiceBusy || importing) return;
    const kws = [...new Set([...keywords, ...splitKeywords(keywordInput)])];
    if (kws.length === 0 && !description.trim()) {
      showToast('请输入关键词或描述想找的内容', 'error');
      return;
    }
    if (kws.join(',').length > 200) {
      showToast('关键词总长度不能超过200字，请把详细需求放入下方描述', 'error');
      return;
    }
    const generation = ++searchGeneration.current;
    searchAbort.current?.abort();
    const abort = new AbortController();
    searchAbort.current = abort;
    const timeout = setTimeout(() => abort.abort(), 240000);
    setLoading(true);
    setJobId('');
    setIntentSummary('');
    setWarnings([]);
    setDiagnostics(undefined);
    setMessage('');
    setItems([]);
    setReviewItems([]);
    setRejectedCount(0);
    setSelected(new Set());
    setImportedIds(new Set());
    try {
      const body = {
        platform,
        keyword: kws.join(','),
        description: description.trim(),
        count,
        date_type: dateType,
        label_type: labelType,
        duration_type: durationType,
        sort_type: sortType,
        note_type: noteType,
        time_filter: timeFilter,
      };
      const fingerprint = JSON.stringify(body);
      if (requestKey.current.fingerprint !== fingerprint) requestKey.current = {fingerprint, id: crypto.randomUUID()};
      const res = await viralFetch({...body, request_id: requestKey.current.id}, abort.signal);
      if (generation !== searchGeneration.current) return;
      setJobId(res.job_id || '');
      setIntentSummary(res.intent_summary || '');
      setWarnings(res.warnings || []);
      setDiagnostics(res.diagnostics);
      setItems(res.items || []);
      setReviewItems(res.review_items || []);
      setRejectedCount(res.rejected_count || 0);
      if (res.message) setMessage(res.message);
      showToast(res.fetched ? `已找到 ${res.fetched} 条，勾选后可导入内容库` : '本次未找到符合条件的内容，请查看检索说明', res.fetched && !res.diagnostics?.partial ? 'success' : 'info');
    } catch (err) {
      if (generation !== searchGeneration.current) return;
      const msg = abort.signal.aborted ? '本次等待超时，可点击查找取回同一任务结果' : err instanceof Error ? err.message : '抓取失败';
      setMessage(msg);
      if (!msg.includes('积分不足')) showToast(msg, 'error');
    } finally {
      clearTimeout(timeout);
      if (generation === searchGeneration.current) setLoading(false);
    }
  }, [keywords, keywordInput, description, loading, voiceBusy, importing, platform, count, dateType, labelType, durationType, sortType, noteType, timeFilter]);

  // ─── 勾选 / 全选 ────────────────────────────────────────────
  const toggleSelect = React.useCallback((id: string) => {
    if (importedIds.has(id)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, [importedIds]);

  const selectableIds = items.map(itemKey).filter((id) => id && !importedIds.has(id));
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggleSelectAll = React.useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        selectableIds.forEach((id) => next.delete(id));
      } else {
        selectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [allSelected, selectableIds]);

  // ─── 一键导入 ───────────────────────────────────────────────
  const handleImport = React.useCallback(async () => {
    const chosen = items.filter((it) => selected.has(itemKey(it)));
    if (chosen.length === 0 || importing) return;
    if (!jobId) { showToast('检索结果已失效，请重新检索', 'error'); return; }
    const generation = searchGeneration.current;
    setImporting(true);
    try {
      const res = await viralImport(jobId, chosen.map(itemKey));
      onImported(res.saved);
      if (generation !== searchGeneration.current) return;
      if (res.failed) {
        showToast(`已导入 ${res.saved} 条，${res.failed} 条未能保存；保留勾选，可重试`, 'error');
        return;
      }
      const savedIds = new Set(chosen.map(itemKey));
      setImportedIds((prev) => {
        const next = new Set(prev);
        savedIds.forEach((id) => next.add(id));
        return next;
      });
      setSelected(new Set());
      showToast(
        res.saved > 0
          ? `已导入 ${res.saved} 条到内容库${res.saved < chosen.length ? `（${chosen.length - res.saved} 条已存在）` : ''}`
          : '所选内容均已存在，未重复导入',
        res.saved > 0 ? 'success' : 'info'
      );
    } catch (err) {
      if (generation !== searchGeneration.current) return;
      const msg = err instanceof Error ? err.message : '导入失败';
      if (!msg.includes('积分不足')) showToast(msg, 'error');
    } finally {
      setImporting(false);
    }
  }, [items, selected, onImported, jobId, importing]);

  if (!open) return null;

  const selectedCount = selected.size;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={handleClose}>
      <div
        className="flex w-full max-w-5xl flex-col overflow-hidden rounded-xl border bg-background shadow-2xl"
        style={{ maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="flex items-center justify-between border-b px-5 py-3.5">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <Zap className="h-4 w-4 text-amber-500" />
            <h3 className="shrink-0 font-semibold tracking-tight">智能抓取</h3>
            <span className="text-xs text-muted-foreground">检索同赛道近期爆款，勾选后一键导入内容库</span>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="关闭爆款检索" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-5">
          {/* 关键词建议（实时，来自赛道关键词） */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-xs font-medium text-foreground">
                赛道关键词建议{suggestLoading ? '（加载中…）' : ''}
              </label>
              {suggestKeywords.length === 0 && !suggestLoading && (
                <span className="text-[11px] text-muted-foreground">暂无关键词，添加监控账号后自动分析生成</span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {suggestKeywords.slice(0, 24).map((k) => {
                const active = keywords.includes(k.word);
                return (
                  <button
                    key={k.word}
                    type="button"
                    onClick={() => toggleSuggest(k.word)}
                    title={`点击${active ? '移除' : '添加'}「${k.word}」到检索栏`}
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                      active
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-accent/40 text-muted-foreground hover:bg-accent'
                    }`}
                  >
                    {k.word}
                    {k.count > 0 && <span className="text-[10px] opacity-60">{k.count}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 关键词检索栏（圆角标签 chips） */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground">关键词</label>
            <div className="flex min-h-[40px] flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5 focus-within:ring-1 focus-within:ring-ring">
              {keywords.map((w) => (
                <span
                  key={w}
                  className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary"
                >
                  {w}
                  <button
                    type="button"
                    onClick={() => removeKeyword(w)}
                    className="text-primary/60 hover:text-primary"
                    title="移除"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <Input
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.nativeEvent.isComposing) return;
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addKeyword(keywordInput);
                  } else if (e.key === 'Backspace' && !keywordInput && keywords.length > 0) {
                    removeKeyword(keywords[keywords.length - 1]);
                  }
                }}
                placeholder={keywords.length === 0 ? '输入关键词后回车，也可在下方描述需求' : '继续输入，回车添加'}
                className="h-7 min-w-[120px] flex-1 border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0"
              />
            </div>
          </div>

          <div>
            <label htmlFor="viral-description" className="mb-2 block text-xs font-medium text-foreground">用一句话描述想找的内容（可选）</label>
            <div className="flex items-start gap-2">
              <textarea ref={descriptionInput} id="viral-description" rows={1} maxLength={1000} value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="例如：找成都的 OPC 或 AI 线下活动，麓湖优先，参考招募文案"
                className="min-h-9 min-w-0 flex-1 resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
              <ViralVoiceInput disabled={loading || importing} onText={appendTranscript} onBusy={setVoiceBusy} />
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">关键词和描述可任选其一；语音识别按平台规则扣积分，文字可编辑后再查找。</p>
          </div>

          {/* 检索维度 */}
          <div className="flex flex-wrap items-end gap-3">
            {/* 平台切换 */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-foreground">平台</label>
              <div className="flex items-center gap-2">
                {(['douyin', 'xiaohongshu'] as const).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPlatform(key)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                      platform === key
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-accent/40 text-muted-foreground hover:bg-accent'
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${PLATFORM_DOT[key]}`} />
                    {platformLabel(key)}
                  </button>
                ))}
              </div>
            </div>

            {/* 时间范围（按平台） */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-foreground">时间范围</label>
              {platform === 'douyin' ? (
                <select
                  value={dateType}
                  onChange={(e) => setDateType(Number(e.target.value))}
                  className="h-9 min-w-[120px] rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value={0}>不限</option>
                  <option value={3}>近3天</option>
                  <option value={7}>近7天</option>
                  <option value={30}>近30天</option>
                </select>
              ) : (
                <select
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.target.value)}
                  className="h-9 min-w-[120px] rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="不限">不限</option>
                  <option value="一天内">一天内</option>
                  <option value="一周内">一周内</option>
                  <option value="半年内">半年内</option>
                </select>
              )}
            </div>

            {/* 爆款标签（仅抖音） */}
            {platform === 'douyin' && (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-foreground">爆款标签</label>
                <select
                  value={labelType}
                  onChange={(e) => setLabelType(Number(e.target.value))}
                  className="h-9 min-w-[120px] rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value={0}>不限</option>
                  <option value={1}>低粉爆款</option>
                  <option value={2}>高完播率</option>
                  <option value={3}>高涨粉率</option>
                  <option value={4}>高点赞率</option>
                </select>
              </div>
            )}

            {/* 时长（仅抖音） */}
            {platform === 'douyin' && (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-foreground">时长</label>
                <select
                  value={durationType}
                  onChange={(e) => setDurationType(Number(e.target.value))}
                  className="h-9 min-w-[120px] rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value={0}>不限</option>
                  <option value={1}>0-15秒</option>
                  <option value={2}>15-60秒</option>
                  <option value={3}>1-5分钟</option>
                  <option value={4}>5分钟以上</option>
                </select>
              </div>
            )}

            {/* 排序（仅小红书） */}
            {platform === 'xiaohongshu' && (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-foreground">排序</label>
                <select
                  value={sortType}
                  onChange={(e) => setSortType(e.target.value)}
                  className="h-9 min-w-[120px] rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="general">综合</option>
                  <option value="time_descending">最新</option>
                  <option value="popularity_descending">最多点赞</option>
                  <option value="comment_descending">最多评论</option>
                  <option value="collect_descending">最多收藏</option>
                </select>
              </div>
            )}

            {/* 笔记类型（仅小红书） */}
            {platform === 'xiaohongshu' && (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-foreground">笔记类型</label>
                <select
                  value={noteType}
                  onChange={(e) => setNoteType(e.target.value)}
                  className="h-9 min-w-[120px] rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="不限">不限</option>
                  <option value="普通笔记">图文</option>
                  <option value="视频笔记">视频</option>
                  <option value="直播笔记">直播</option>
                </select>
              </div>
            )}

            {/* 抓取数量 */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-foreground">目标数量（最多）</label>
              <select
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                className="h-9 min-w-[120px] rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {[10, 20, 30, 40, 50].map((n) => (
                  <option key={n} value={n}>{n} 条</option>
                ))}
              </select>
            </div>

            {/* 立即爬取 */}
            <Button onClick={handleFetch} disabled={loading || voiceBusy || importing} className="h-9">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Zap className="h-4 w-4 mr-1" />}
              {loading ? '理解并检索中…' : '查找爆款内容'}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">按所选搜索档位计费，符合条件的内容可能少于目标数量。描述中指定的时间优先于上方时间选项。</p>
          <p className="text-xs text-muted-foreground">计费标准：10条消耗3积分，20条消耗4积分，30条消耗5积分，40条消耗6积分，50条消耗7积分</p>

          {intentSummary && <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm"><span className="font-medium">本次理解：</span>{intentSummary}</div>}
          {warnings.map((warning, i) => <p key={i} role="status" className="text-xs text-amber-700 dark:text-amber-400">{warning}</p>)}
          {diagnostics && <p role="status" className="text-xs text-muted-foreground">取回 {diagnostics.candidate_count} 条候选 · 符合 {diagnostics.qualified_count} 条 · {diagnostics.partial ? '部分检索未完成' : STOP_REASONS[diagnostics.stop_reason] || '本次检索已结束'}</p>}
          {diagnostics?.degraded && <p role="status" className="text-xs text-amber-600 dark:text-amber-400">{degradedLabel(diagnostics.degraded_groups)}</p>}
          {message && (
            <div className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
              <AlertCircle className="h-3.5 w-3.5" />
              {message}
            </div>
          )}

          {reviewItems.length > 0 && (
            <details className="rounded-md border border-amber-500/40 p-3">
              <summary className="cursor-pointer text-sm font-medium">可能相关，待核实（{reviewItems.length} 条）</summary>
              <p className="mt-2 text-xs text-muted-foreground">以下内容尚不能确认满足全部条件，可查看缺少的证据和原文；暂不支持一键导入。</p>
              <div className="mt-3 max-h-80 space-y-3 overflow-y-auto">
                {reviewItems.map((it, i) => (
                  <div key={it.item_id || it.note_id || i} className="rounded border p-3 text-sm">
                    <p className="font-medium">{it.title || '标题缺失'}</p>
                    {it.desc && <p className="mt-1 line-clamp-3 text-xs text-muted-foreground">{it.desc}</p>}
                    <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">{it.match_reasons?.join(' · ') || '内容信息不足，尚待核实'}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{it.nickname || it.author || '作者未知'} · 点赞 {it.likes == null ? '—' : it.likes}</p>
                    {it.url && /^https:\/\//i.test(it.url) && <a className="mt-1 inline-block text-xs text-primary underline" href={it.url} target="_blank" rel="noopener noreferrer">查看原文</a>}
                  </div>
                ))}
              </div>
            </details>
          )}
          {rejectedCount > 0 && <p className="text-xs text-muted-foreground">另有 {rejectedCount} 条明确不符合条件，已排除（如时间超出范围、异地或命中排除条件）。</p>}

          {/* 瀑布流结果 */}
          {items.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">
                  抓取结果（{items.length} 条）
                  {selectedCount > 0 && <span className="ml-2 text-xs text-primary">已选 {selectedCount} 条</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={toggleSelectAll}>
                    {allSelected ? <Square className="h-3.5 w-3.5" /> : <CheckSquare className="h-3.5 w-3.5" />}
                    {allSelected ? '取消全选' : '全选'}
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setItems([]); setSelected(new Set()); setImportedIds(new Set()); }}>
                    清空
                  </Button>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {items.map((it, idx) => {
                  const id = itemKey(it) || `_${idx}`;
                  const isSelected = selected.has(id);
                  const isImported = importedIds.has(id);
                  const cover = it.thumbnail || it.cover;
                  return (
                    <div
                      key={id}
                      className={`group relative overflow-hidden rounded-lg border bg-card transition-all ${
                        isSelected ? 'border-primary ring-1 ring-primary/50' : ''
                      } ${isImported ? 'opacity-60' : 'cursor-pointer hover:shadow-md'}`}
                      onClick={() => !isImported && setPreview(it)}
                    >
                      {/* 勾选框 */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleSelect(id); }}
                        disabled={isImported}
                        title={isImported ? '已导入内容库' : isSelected ? '取消选择' : '选择此内容'}
                        className={`absolute left-1.5 top-1.5 z-10 rounded p-0.5 transition-colors ${
                          isSelected ? 'bg-primary text-primary-foreground' : 'bg-black/40 text-white hover:bg-black/60'
                        } ${isImported ? 'bg-gray-600/80 cursor-not-allowed' : ''}`}
                      >
                        {isImported ? <CheckSquare className="h-4 w-4" /> : isSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                      </button>
                      {/* 已导入角标 */}
                      {isImported && (
                        <span className="absolute right-1.5 top-1.5 z-10 rounded bg-emerald-600/90 px-1.5 py-0.5 text-[10px] font-medium text-white">
                          已导入
                        </span>
                      )}
                      {/* 封面 */}
                      <div className="relative w-full bg-muted" style={{ aspectRatio: '3/4' }}>
                        {cover ? (
                          <img src={cover} alt="" referrerPolicy="no-referrer" loading="lazy" className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full items-center justify-center">
                            <Video className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                        {it.is_low_fans_but_hot && (
                          <span className="absolute left-1.5 top-8 rounded bg-orange-500/90 px-1.5 py-0.5 text-[10px] font-medium text-white">
                            低粉爆款
                          </span>
                        )}
                        {(it.platform || platform) === 'douyin' && it.duration ? (
                          <span className="absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1 py-0.5 text-[10px] text-white">
                            {fmtDur(it.duration)}
                          </span>
                        ) : null}
                        <span className="absolute bottom-1.5 left-1.5">
                          <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium text-white ${(it.platform || platform) === 'douyin' ? 'bg-sky-500/90' : 'bg-pink-500/90'}`}>
                            {platformLabel(it.platform || platform)}
                          </span>
                        </span>
                      </div>
                      {/* 信息 */}
                      <div className="p-2.5">
                        <div className="line-clamp-2 text-xs font-medium leading-relaxed">{it.title}</div>
                        {!!it.match_reasons?.length && <p className="mt-1 text-[11px] text-primary">{it.match_reasons.join(" · ")}</p>}
                        <div className="mt-1.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                          <span className="truncate">{it.nickname || it.author || ''}</span>
                          {typeof it.fans === 'number' && it.fans > 0 && <span>{fmtNum(it.fans)}粉</span>}
                        </div>
                        <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                          {typeof it.play_count === 'number' && it.play_count > 0 && (
                            <span className="inline-flex items-center gap-0.5"><Eye className="h-3 w-3" />{fmtNum(it.play_count)}</span>
                          )}
                          <span className="inline-flex items-center gap-0.5"><Heart className="h-3 w-3" />{fmtNum(it.likes)}</span>
                          {typeof it.comments === 'number' && it.comments > 0 && (
                            <span className="inline-flex items-center gap-0.5"><MessageSquare className="h-3 w-3" />{fmtNum(it.comments)}</span>
                          )}
                          {typeof it.collected === 'number' && it.collected > 0 && (
                            <span className="inline-flex items-center gap-0.5"><Bookmark className="h-3 w-3" />{fmtNum(it.collected)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* 底部操作栏 */}
        <div className="flex items-center justify-between gap-3 border-t bg-muted/20 px-5 py-3">
          <span className="text-xs text-muted-foreground">
            {items.length === 0
              ? '点击「立即爬取爆款」检索同赛道近期爆款（扣除积分）'
              : selectedCount > 0
                ? `已勾选 ${selectedCount} 条，导入后可在内容库中预览、转录、改写`
                : '勾选任意内容后可一键导入内容库'}
          </span>
          <Button onClick={handleImport} disabled={selectedCount === 0 || importing} className="h-9">
            {importing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckSquare className="h-4 w-4 mr-1" />}
            {importing ? '导入中…' : '一键导入内容库'}
          </Button>
        </div>
      </div>

      {/* ─── 爆款预览弹窗（哪怕只有封面也可预览） ─── */}
      {preview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => setPreview(null)}>
          <div className="flex w-full max-w-2xl flex-col overflow-hidden rounded-xl border bg-background shadow-2xl" style={{ maxHeight: '85vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-5 py-3.5">
              <h4 className="font-semibold tracking-tight">爆款预览</h4>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setPreview(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="overflow-y-auto p-5 space-y-4">
              {/* 封面大图 */}
              {(preview.thumbnail || preview.cover) ? (
                <div className="overflow-hidden rounded-lg border" style={{ maxHeight: '400px' }}>
                  <img
                    src={preview.thumbnail || preview.cover}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="mx-auto h-full max-h-[400px] w-auto object-contain"
                  />
                </div>
              ) : (
                <div className="flex h-40 items-center justify-center rounded-lg border bg-muted">
                  <Video className="h-8 w-8 text-muted-foreground" />
                </div>
              )}
              {/* 信息 */}
              <div>
                <h4 className="font-medium">{preview.title}</h4>
                <div className="mt-2 flex flex-wrap gap-3 text-sm text-muted-foreground">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${(preview.platform || platform) === 'douyin' ? 'bg-sky-500/10 text-sky-600 dark:text-sky-400' : 'bg-pink-500/10 text-pink-600 dark:text-pink-400'}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${(preview.platform || platform) === 'douyin' ? 'bg-sky-500' : 'bg-pink-500'}`} />
                    {platformLabel(preview.platform || platform)}
                  </span>
                  {preview.nickname || preview.author ? <span>作者：{preview.nickname || preview.author}</span> : null}
                  {typeof preview.fans === 'number' && preview.fans > 0 && <span>{fmtNum(preview.fans)} 粉丝</span>}
                  {typeof preview.play_count === 'number' && preview.play_count > 0 && (
                    <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{fmtNum(preview.play_count)} 播放</span>
                  )}
                  <span className="inline-flex items-center gap-1"><Heart className="h-3.5 w-3.5" />{fmtNum(preview.likes)} 点赞</span>
                  {typeof preview.comments === 'number' && preview.comments > 0 && (
                    <span className="inline-flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />{fmtNum(preview.comments)} 评论</span>
                  )}
                  {typeof preview.collected === 'number' && preview.collected > 0 && (
                    <span className="inline-flex items-center gap-1"><Bookmark className="h-3.5 w-3.5" />{fmtNum(preview.collected)} 收藏</span>
                  )}
                </div>
              </div>
              {/* 描述 */}
              {(preview.desc || preview.title) && (
                <div>
                  <h5 className="mb-1 text-sm font-medium">文案</h5>
                  <p className="whitespace-pre-wrap text-sm text-muted-foreground">{preview.desc || preview.title}</p>
                </div>
              )}
              {/* 操作 */}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => {
                    const id = itemKey(preview);
                    if (id && !importedIds.has(id)) {
                      toggleSelect(id);
                      setPreview(null);
                    }
                  }}
                  disabled={!itemKey(preview) || importedIds.has(itemKey(preview))}
                >
                  <CheckSquare className="h-4 w-4 mr-1" />
                  {importedIds.has(itemKey(preview)) ? '已导入内容库' : selected.has(itemKey(preview)) ? '取消勾选' : '勾选并导入'}
                </Button>
                {preview.url && (
                  <Button variant="outline" size="sm" onClick={() => window.open(preview.url, '_blank', 'noopener')}>
                    <ExternalLink className="h-4 w-4 mr-1" />
                    打开原文
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
